import { useEffect, useRef } from 'react';
import { Box, Text, ScrollArea, Group, Badge, ActionIcon, Tooltip } from '@mantine/core';
import { IconMicrophone, IconMicrophoneOff } from '@tabler/icons-react';
import { useConversaStore } from '@/stores/conversaStore';
import { useAuthStore } from '@/stores/authStore';
import { ConversaMessage } from './ConversaMessage';
import { ConversaInput } from './ConversaInput';

interface ConversaPanelProps {
  onSendChatMessage: (text: string) => void;
  isOwner?: boolean;
  isTranscribing?: boolean;
  onStartTranscription?: () => void;
  onStopTranscription?: () => void;
}

export const ConversaPanel = ({
  onSendChatMessage,
  isOwner = false,
  isTranscribing = false,
  onStartTranscription,
  onStopTranscription,
}: ConversaPanelProps) => {
  const messages = useConversaStore((state) => state.messages);
  const user = useAuthStore((state) => state.user);
  const viewport = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (viewport.current) {
      viewport.current.scrollTo({
        top: viewport.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  const handleTranscriptionToggle = () => {
    if (isTranscribing) {
      onStopTranscription?.();
    } else {
      onStartTranscription?.();
    }
  };

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with transcription control */}
      <Box
        p="sm"
        style={{
          borderBottom: '1px solid #e9ecef',
          backgroundColor: '#f8f9fa',
        }}
      >
        <Group justify="space-between">
          <Group gap="xs">
            {messages.length > 0 && (
              <Badge size="sm" variant="light" color="blue">
                {messages.length}
              </Badge>
            )}
          </Group>
          {isOwner && onStartTranscription && onStopTranscription && (
            <Tooltip
              label={isTranscribing ? 'Parar transcrição' : 'Iniciar transcrição'}
              position="left"
            >
              <ActionIcon
                variant={isTranscribing ? 'filled' : 'light'}
                color={isTranscribing ? 'red' : 'blue'}
                size="lg"
                onClick={handleTranscriptionToggle}
                aria-label={isTranscribing ? 'Parar transcrição' : 'Iniciar transcrição'}
              >
                {isTranscribing ? (
                  <IconMicrophoneOff size={20} />
                ) : (
                  <IconMicrophone size={20} />
                )}
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Box>

      {/* Messages area */}
      <ScrollArea
        style={{ flex: 1 }}
        viewportRef={viewport}
        p="sm"
      >
        {messages.length === 0 ? (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 200,
            }}
          >
            <Text c="dimmed" size="sm" ta="center">
              As mensagens do chat e transcrições aparecerão aqui.
            </Text>
          </Box>
        ) : (
          messages.map((message) => (
            <ConversaMessage
              key={message.id}
              message={message}
              isOwnMessage={message.senderName === user?.name}
            />
          ))
        )}
      </ScrollArea>

      {/* Input */}
      <ConversaInput onSendMessage={onSendChatMessage} />
    </Box>
  );
};
